import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2018 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { component_identity, IdentityUtils } from "abreact/core/identity/IdentityUtils";
import { ComponentPageState } from "abreact/core/pagestate/ComponentPageState";
import { ComponentPageStateConfigManager } from "abreact/core/pagestate/ComponentPageStateConfigManager";
import { ComponentPageStateConfig } from "abreact/core/pagestate/IComponentPageStateConfig";
import { createPageStorageIdentity } from "abreact/core/pagestate/IPageStateStorageIdentity";
import { PageStateLogger } from "abreact/core/pagestate/PageStateLogger";
import { RenderWithPageState } from "abreact/core/pagestate/RenderWithPageState";
import { StorageCategory } from "abreact/core/pagestate/StorageCategory";
import { NavigationService } from "abreact/incubator/ui/app/NavigationService";
import { AbReactServices } from "abreact/service/AbReactServices";
import * as AbHooks from "abreact/utils/AbHooks";
import { ClassUtils } from "abweb/utils/ClassUtils";
import { LogUtils } from "abweb/utils/log/LogUtils";
import * as React from "react";

/*
Define the content of the context
*/

export const PageStateStorageIdentityContext = /*#__PURE__*/React.createContext(createPageStorageIdentity(StorageCategory.NONE, "none"));
PageStateStorageIdentityContext.displayName = "PageStateStorageIdentityContext";

/**
 * Source of most of the critical page state functions and methods. Also see
 * IdentityUtils and RenderWithPageState.
 */
export class PageStateTools {
  static maybeGetComponentPageStateConfig(component) {
    return ComponentPageStateConfigManager.getConfigForComponent(component);
  }
  static getComponentPageStateConfig(component) {
    const config = PageStateTools.maybeGetComponentPageStateConfig(component);
    if (!config) {
      throw new Error("component not properly configured: " + IdentityUtils.isIdentifiable(component) ? IdentityUtils.convertToIdentifiable(component)[component_identity].qualifiedId : component);
    }
    return config;
  }

  /**
   * This method creates new class that is an extension of the original class that has an
   * instrumented render method, adding component identity and page state identity via
   * contexts.
   *
   * Many, many attempts at doing this differently were tried (see failed implementations
   * as comments at the bottom of this file), and this was the only one that worked. Of particular
   * importance is the use of a component with context that is an observer as the component
   * that ultimately emits the content.
   *
   * @param target
   * @param usesMobx
   */
  static hasPageStateInternalHOC(target, usesMobx) {
    if (!ClassUtils.isInProtoChain(target, React.Component)) {
      throw new Error("Cannot apply reloadable to a class that does not extend React.Component.");
    }
    let anyTarget = target;
    class PageStateWrapper extends anyTarget {
      // Needed to pass this valuew to the constructor function called
      // on each instance
      get abInternal_pageState_isMobx() {
        return usesMobx;
      }
      render() {
        PageStateTools.getComponentPageStateConfig(this).isCollectingDefaults = false;
        return /*#__PURE__*/React.createElement(RenderWithPageState, {
          component: this,
          useMobx: usesMobx,
          renderContent: () => super.render()
        });
      }
      componentWillUnmount() {
        IdentityUtils.unRegisterFromParent(this);
        if (super.componentWillUnmount) {
          super.componentWillUnmount();
        }
      }
    }
    _defineProperty(PageStateWrapper, "displayName", anyTarget.name);
    IdentityUtils.makeComponentIdentifiable(PageStateWrapper.prototype);
    return PageStateWrapper;
  }
  static initializePageStateStorageForRender(identifiableComponent, pageStorageIdentity) {
    /*
    Only set the state storage on the first render. It will have been initialized in the
    constructor to have page state configuration, but the page state storage can only
    be set in the render method once identity is known (obtained from context, which is
    only available in the render method)
     */

    const pageStateConfiguration = PageStateTools.getComponentPageStateConfig(identifiableComponent);
    /*
     This is a sign that there is no infrastructure in place, so don't attempt to use page state. This might be a sampler
     or a unit test.
     */
    if (!pageStateConfiguration.componentPageState) {
      let pageStateStorage;
      let componentPageState;
      // Don't look if the category is still NONE. That means there is missing infrastructure above this invocation,
      // perhaps in a test case or some other scenario (like a sampler)
      if (pageStorageIdentity.category !== StorageCategory.NONE) {
        pageStateStorage = AbReactServices.getPageStateManager().getPageInstanceStateStorage(pageStorageIdentity);
        if (pageStateStorage != null) {
          componentPageState = pageStateStorage.getOrCreateComponentPageState(identifiableComponent, pageStateConfiguration.useMobx);
        } else {
          // there should be page state storage if the category is not NONE, but to keep things from
          // breaking, log and create local storage for the component.
          PageStateTools.LOG.warn("Unable to find page state storage for identity: " + pageStorageIdentity.key);
        }
      }
      if (pageStateStorage) {
        pageStateConfiguration.setPageInstanceStateStorage(pageStateStorage);
      }
      if (componentPageState) {
        pageStateConfiguration.setComponentPageState(componentPageState);
      } else {
        PageStateTools.setEmptyLocalComponentPageStateOnConfig(pageStateConfiguration, identifiableComponent);
      }
    }
  }

  /*
  The config is stored in a weak map with teh component as the key, so
  this local component state storage object will go away when the component is garbage collected
  */
  static setEmptyLocalComponentPageStateOnConfig(config, component, qualifiedId) {
    config.setComponentPageState(new ComponentPageState(component || null, qualifiedId, config.useMobx));
    PageStateLogger.maybeLogFor("state", component, "using local storage only");
  }
  static getPageStateInfo(component) {
    let pageStateAnnotationInfo;
    if (component[PageStateTools.PAGE_STATE_FIELD_INFO_PROPERTY]) {
      pageStateAnnotationInfo = component[PageStateTools.PAGE_STATE_FIELD_INFO_PROPERTY];
    }
    return pageStateAnnotationInfo;
  }
  static applyInformationCollectedFromAnnotationToField(target, info) {
    let initialValue;
    if (target[info.field] !== undefined) {
      initialValue = target[info.field];
    }
    if (delete target[info.field]) {
      PageStateTools.rewriteFieldAsPageState(target, info.field, info.propertyDescriptor, info.isSilent, initialValue);
    } else {
      PageStateTools.LOG.warn(`Unable to apply delete field ${info.field} to apply page state annotation on class ${ClassUtils.getClassname(target)}`);
    }
  }
  static addPageStateInfo(component, info) {
    let existingPageStateInfo = PageStateTools.getPageStateInfo(component);
    if (!existingPageStateInfo) {
      existingPageStateInfo = [];
      component[PageStateTools.PAGE_STATE_FIELD_INFO_PROPERTY] = existingPageStateInfo;
    }
    existingPageStateInfo.push(info);
  }
  static initializeIdentityAndConfigurationFromConstructor(component) {
    IdentityUtils.makeComponentIdentifiable(component);
    let configuration = PageStateTools.maybeGetComponentPageStateConfig(component);
    if (!configuration) {
      configuration = new ComponentPageStateConfig(IdentityUtils.getComponentIdentity(component).name);
      ComponentPageStateConfigManager.setConfigForComponent(component, configuration);
      configuration.useMobx = component[PageStateTools.PAGE_STATE_IS_MOBX_PROPERTY];
    }
    return configuration;
  }
  static initializeComponentFromConstructor(component) {
    PageStateTools.initializeIdentityAndConfigurationFromConstructor(component);
    let infos = PageStateTools.getPageStateInfo(component);
    if (infos) {
      infos.forEach(info => PageStateTools.applyInformationCollectedFromAnnotationToField(component, info));
    }
  }

  /**
   * Null save way of getting the qualified id off the identity on the component
   *
   * @param {IIdentifiableComponent | null | undefined} component
   * @returns {string | null}
   */
  static getQualifiedId(component) {
    if (component && component[component_identity] && component[component_identity].qualifiedId) {
      return component[component_identity].qualifiedId;
    } else {
      return null;
    }
  }
  static determineStorageCategory(routeRenderContext) {
    return NavigationService.getSingleton().mainAreaRouteRenderContext === routeRenderContext ? StorageCategory.PRIMARY : StorageCategory.SECONDARY;
  }

  /**
   * Established a member variable as page state that will be restored on a reload.
   */
  static rewriteFieldAsPageState(target, key, originalDescriptor, isSilent, initialValue) {
    // transpilers are inconsistent in handling decorators.
    //  babel will pass in the originalDescriptor (with an initializer set to the default value).
    //  the TypeScript compiler will not pass the original descriptor, but will set the default value in the constructor
    //
    // With babel, the returned propertyDescriptor is used to define the property on the instance.
    // With the TypeScript, Object.defineProperty called here will define the property on the prototype.
    // Therefore, we do both - defineProperty, and return the descriptor.
    // See https://confluence.abinitio.com/display/ABSDKTEAM/Property+initialization+and+decorators
    let propertyDescriptor;

    // hold the initializer value (if available), in an object
    // so that its value can be read and cleared while referenced in
    // a closure from setter and getter functions below.
    let initialValueToUse;
    if (initialValue !== undefined) {
      initialValueToUse = initialValue;
    } else {
      initialValueToUse = originalDescriptor && originalDescriptor.initializer ? originalDescriptor.initializer() : undefined;
    }
    if (initialValueToUse) {
      const config = PageStateTools.getComponentPageStateConfig(target);
      if (!isSilent) {
        config.setDefaultValue(key, initialValueToUse);
      } else {
        config.setSilentDefaultValue(key, initialValueToUse);
      }
    }
    let getter = function () {
      return PageStateTools.getComponentPageStateConfig(this).getValue(key);
    };
    let setter;
    if (!isSilent) {
      setter = function (newVal) {
        const config = PageStateTools.getComponentPageStateConfig(this);
        config.setValue(key, newVal, !config.useMobx ? () => this.forceUpdate.apply(this) : undefined);
      };
    } else {
      setter = function (newVal) {
        const config = PageStateTools.getComponentPageStateConfig(this);
        config.setSilentValue(key, newVal);
      };
    }

    // need to remove the variable accessor and redefine it with the new get/set property
    if (delete target[key]) {
      // Create new property with getter and setter
      propertyDescriptor = {
        get: getter,
        set: setter,
        enumerable: true,
        configurable: true
      };
      Object.defineProperty(target, key, propertyDescriptor);
    }
    return propertyDescriptor;
  }
  static usePageStateSupportInternal(props, componentName, key) {
    let pageStateContext;
    let stateStorageAccessor;

    // TODO: refactor so this hook does not live inside a Class
    // Disabling this lint rule since we know this is only used by functional components
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const wrapperForceUpdate = AbHooks.useForceUpdate();

    // Disabling this lint rule since we know this is only used by functional components
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const pageStateStorageIdentity = React.useContext(PageStateStorageIdentityContext);

    // Disabling this lint rule since we know this is only used by functional components
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const contextRef = React.useRef();

    // Disabling this lint rule since we know this is only used by functional components
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const componentIdentitySupport = IdentityUtils.useComponentIdentitySupportInternal(props, componentName, key);
    if (contextRef.current != null) {
      pageStateContext = contextRef.current;
      const storageAccessor = pageStateContext.stateStorageAccessor;
      // just reset the currentIndex since everything else remains the same
      if (storageAccessor) {
        pageStateContext = {
          ...pageStateContext,
          stateStorageAccessor: {
            ...storageAccessor,
            currentIndex: 0
          }
        };
      }
      contextRef.current = pageStateContext;
    } else {
      // no point in computing the rest if there isn't an identity for the component
      if (componentIdentitySupport?.identity) {
        const componentIdentity = componentIdentitySupport.identity;
        if (pageStateStorageIdentity) {
          const pageStateStorage = AbReactServices.getPageStateManager().getPageInstanceStateStorage(pageStateStorageIdentity);
          if (pageStateStorage) {
            const component = {
              [component_identity]: componentIdentity
            };
            const stateStorage = pageStateStorage.getOrCreateComponentPageState(component, false);
            stateStorageAccessor = {
              storage: stateStorage,
              currentIndex: 0,
              updater: wrapperForceUpdate
            };
          }
        }
        pageStateContext = {
          identity: componentIdentity,
          stateStorageAccessor: stateStorageAccessor
        };
        contextRef.current = pageStateContext;
      }
    }
    return pageStateContext ?? {};
  }
  static usePageStateInternal(isSilent, pageStateContext, initialState) {
    if (pageStateContext?.stateStorageAccessor) {
      const stateStorageAccessor = pageStateContext.stateStorageAccessor;
      const currentIndex = stateStorageAccessor.currentIndex++;
      const valueKey = "_" + currentIndex;
      const value = stateStorageAccessor.storage?.get(valueKey) ?? initialState;
      const setValue = value => {
        if (stateStorageAccessor.storage?.get(valueKey) !== value) {
          stateStorageAccessor.storage?.set(valueKey, value);
          !isSilent && stateStorageAccessor.updater();
        }
      };
      return [value, setValue];
    } else {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      return React.useState(initialState);
    }
  }
}

// OLD FAILED ATTEMPTS WORTH KEEPING AROUND FOR REFERENCE
/**
 * FAILED ATTEMPT: This did not work because the emitted component is not an extension of the wrapped component, so it
 * loses all inherited methods (even though inheritance for react components is discouraged, it is used) and static members
 *
 * @param WrappedComponent
 * @param useMobx
 * @private
 */
// private static createPageStateInternalHOCPropsProxy<P extends object>(WrappedComponent: ComponentType<P>, useMobx: boolean): ComponentType<P & PageStateHOCProps>
// {
//
//   const InstrumentedWrappedComponent = PageStateTools.replaceConstructorWithInstrumentedVersion((WrappedComponent as any))
//
//   const WrapperHOC = class PageStateHOC extends React.Component<P & PageStateHOCProps> {
//     // ensures this wrapper appears in qualifiedIds (and component browser) as the original wrapped component
//     static displayName: string = WrappedComponent.name;
//
//     public get abInternal_pageState_isMobx(): boolean {
//       return useMobx;
//     }
//
//     constructor(props: P & PageStateHOCProps) {
//       super(props);
//       PageStateLogger.maybeLog("system", `in HOC constructor for ${WrapperHOC.displayName} differentiator=${IdentityUtils.findComponentDifferentiator(this)} className=${(props as any)?.className}`)
//       /*
//       Makes this instance identifiable and establishes its page state config
//       ComponentPageState will actually be created for this wrapper but made available via a prop for the actual component
//       which has the "instrumented" getter / setter fields to read from it
//
//       Does NOT turn off collecting of defaults since that will be turned off when the wrapped component is constructed
//       */
//       PageStateTools.initializeIdentityAndConfigurationFromConstructor(this);
//     }
//
//     override render()
//     {
//       PageStateLogger.maybeLog("system", `in HOC render for ${WrapperHOC.displayName} differentiator=${IdentityUtils.findComponentDifferentiator(this)} className=${(this.props as any)?.className}`)
//       const identifiableComponent = IdentityUtils.convertToIdentifiable(this);
//       const hocIdentity = IdentityUtils.getComponentIdentity(identifiableComponent);
//       return <PageStateStorageIdentityContext.Consumer>
//         {
//           (pageStateStorageIdentity) => {
//             return <IdentityContext.Consumer>
//               {(parentIdentityContext) => {
//                 // needs some cleaning up...
//                 if (!hocIdentity.qualifiedId) {
//                   IdentityUtils.initializeIdentity(identifiableComponent,
//                     parentIdentityContext?.componentIdentity ?? IdentityUtils.createPlaceHolderParentIdentity(identifiableComponent));
//                   PageStateTools.initializePageStateStorageForRender(identifiableComponent, pageStateStorageIdentity)
//                 }
//
//                 return <IdentityContext.Provider
//                   value={{componentIdentity: hocIdentity}}>
//                   {
//                     <InstrumentedWrappedComponent {...this.props as P}
//                                                   componentPageStateConfig={PageStateTools.getComponentPageStateConfig(identifiableComponent)}/>
//                   }
//                 </IdentityContext.Provider>
//               }
//               }
//             </IdentityContext.Consumer>
//           }
//         }
//       </PageStateStorageIdentityContext.Consumer>
//     }
//   };
//
//   if (useMobx)
//   {
//     observer((WrappedComponent as any));
//   }
//
//   return WrapperHOC;
// }
//
// private static replaceConstructorWithInstrumentedVersion<T extends { new (args: any): {} }>(constructor: T): T {
//   const originalConstructor = constructor;
//
//   const instrumentedConstructor = function (args: any) {
//     const instance = new originalConstructor(args);
//
//     // turn off default collection
//     PageStateTools.getComponentPageStateConfig(instance).isCollectingDefaults = false;
//
//     return instance;
//   };
//
//   instrumentedConstructor.prototype = originalConstructor.prototype;
//
//   return instrumentedConstructor as any;
// }

/*
FAILED ATTEMPT:
This did not work since replacing the render method conflicted with mobx replacing teh render method
On first render, it uses this one and then it replaces it with a "reactiveRender" that does not have this tooling:

*/
// public static hasPageStateInternal(target: any, usesMobx: boolean): any {
//
//   if (!ClassUtils.isInProtoChain(target, React.Component)) {
//     throw new Error("Cannot apply reloadable to a class that does not extend React.Component.");
//   }
//   PageStateLogger.maybeLogFor("system", target, "hasPageStateInternal being applied");
//   const anyTarget = (target as any);
//   const anyTargetPrototype = (anyTarget.prototype as any);
//
//   /*
//   Stash this on the component so we can decide whether to use an observable map in the storage
//   later in the constructor;
//    */
//   anyTargetPrototype[PageStateTools.PAGE_STATE_IS_MOBX_PROPERTY] = usesMobx;
//
//   /*
//   Snag the render method from the prototype, which is the actual prototype for the
//   component. The target is just a constructor function and does not have a render method.
//
//   Example of the prototype of the constructor function "target" passed into the decorator:
//
//     Component {abInternal_pageState_field_info: Array(1), render: ƒ, setColor: ƒ, createColorDiv: ƒ}
//     abInternal_pageState_field_info
//     :
//     [{…}]
//     constructor
//     :
//     class TestPageStateClassComponent
//     createColorDiv
//     :
//     ƒ createColorDiv()
//     render
//     :
//     render() {content of component render method...}
//
//    Note: Need to get this version of the render method before it is rewritten to avoid infinite loops
//    This is what will ultimately be invoked after the page state preparation code is finished
//    */
//   const originalComponentRenderMethod = anyTargetPrototype.render;
//
//   /*
//   The same is true of the component will unmount function
//    */
//   const originalComponentWillUnmountMethod = anyTargetPrototype.componentWillUnmount;
//
//   /*
//   Replace the protoype's render method with an unbound function that sets up the page state machinery
//   Note: using fat arrow syntax here would bind it to the current this, which is this function
//    */
//   anyTargetPrototype.render = function (): React.ReactNode {
//     /*
//     Since this method is not bound, the "this" will be the component
//      */
//     return <PageStateStorageIdentityContext.Consumer>
//       {
//         (pageStateStorageIdentity) => {
//           return IdentityUtils.renderWithIdentity(this,
//             () => originalComponentRenderMethod.bind(this)(),
//             (identifiableComponent) => PageStateTools.initializePageStateStorageForRender(identifiableComponent, pageStateStorageIdentity))
//         }
//       }
//     </PageStateStorageIdentityContext.Consumer>
//   }
//
//   /**
//    * Swap out the componentWillUnmount to handle the page state cleanup and then, if there was one,
//    * invoke the original componentWillUnmount
//    *
//    * Note: using fat arrow syntax here would bind it to the current this, which is this function
//    */
//   anyTarget.prototype.componentWillUnmount = function unmountFunction() {
//     // can't do this or all the state will be gone by the time the store call is made at the top
//     // const config = PageStateTools.getComponentPageStateConfig(this);
//     // if (config.componentPageState) {
//     //   const pageStateStorage = config.pageInstanceStateStorage
//     //   if (pageStateStorage) {
//     //     pageStateStorage.removeStorage(config.componentPageState);
//     //   }
//     // }
//
//     IdentityUtils.unRegisterFromParent(this);
//
//     if (originalComponentWillUnmountMethod) {
//       originalComponentWillUnmountMethod.bind(this)();
//     }
//   };
//
//   IdentityUtils.makeComponentIdentifiable(target.prototype);
//
//   return target;
//
// }
_defineProperty(PageStateTools, "LOG", LogUtils.getLogger(__filename));
_defineProperty(PageStateTools, "PAGE_STATE_FIELD_INFO_PROPERTY", "abInternal_pageState_field_info");
_defineProperty(PageStateTools, "PAGE_STATE_IS_MOBX_PROPERTY", "abInternal_pageState_isMobx");
//# sourceMappingURL=PageStateTools.js.map
